import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { TestStatusHistoryModal } from '@pages/Profile/W8Beneficial/modals/TestStatusHistory.modal'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionEntity } from '@context/EntityContext'
import { useModal } from '@context/ModalsContext'
import { useArabicSessionLanguage, useSessionLanguage } from '@context/SessionSettingsContext'

import { AlertNotice } from '@ui/components/AlertNotice'
import { Button } from '@ui/components/Button'
import { PageSection } from '@ui/components/PageSection'
import { ArrowCircleIcon } from '@ui/icons/ArrowCircle.icon'
import { ClockIcon } from '@ui/icons/Clock.icon'
import { KebabIcon } from '@ui/icons/Kebab.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { CommonPaths } from '@utils/path'

import { AccountDetailedDto } from '@model/AccountDetailedDto'
import { TestAnswersDto } from '@model/ClientTestAnswerDto'
import { ClientTestDto } from '@model/ClientTestDto'
import { ClientTestSubcategoryEnum } from '@model/ClientTestSubcategoryEnum'
import { RegulatorOptionDto } from '@model/RegulatorOptionDto'
import { TaxHistoryDto } from '@model/TaxHistoryDto'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import {
  StatusTemplateBody,
  StatusTemplateButtons,
  StatusTemplateWrapper,
} from '@shared/templates/StatusTemplate/StatusTemplate'

import { W8BeneficialForm, W8BeneficialFormValues } from './W8BeneficialForm'
import { useDownloadW8BForm } from './useDownloadW8BForm'

export const W8BeneficialPage: React.FC = () => {
  useScrollToTop()
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const entity = useSessionEntity()
  const locale = useSessionLanguage()

  const { data: testHistory = [], isLoading: isLoadingTaxHistory } = useQuery({
    queryKey: queryKeys.profile.taxHistory(locale),
    queryFn: () => apiClient.getTaxHistory(locale),
  })

  const { data: regulators = [], isLoading } = useQuery({
    queryKey: queryKeys.regulators(entity, locale),
    queryFn: () => apiClient.getRegulators(entity, locale),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  })

  const { data: taxFormQuestions, isLoading: isLoadingTaxFormQuestions } = useQuery({
    queryKey: queryKeys.profile.taxFormQuestions(ClientTestSubcategoryEnum.W8BENTaxForm, locale),
    queryFn: () =>
      apiClient.getTaxFormQuestions({
        subcategoryId: ClientTestSubcategoryEnum.W8BENTaxForm,
        languageId: locale,
      }),
  })

  const { data: taxFormAnswers, isLoading: isLoadingTaxFormAnswers } = useQuery({
    queryKey: queryKeys.profile.taxFormAnswers(ClientTestSubcategoryEnum.W8BENTaxForm, locale),
    queryFn: () =>
      apiClient.getTaxFormAnswers({
        subcategoryId: ClientTestSubcategoryEnum.W8BENTaxForm,
        languageId: locale,
      }),
  })

  return (
    <W8Beneficial
      account={account}
      isLoading={
        isLoadingTaxFormAnswers || isLoadingTaxFormQuestions || isLoading || isLoadingTaxHistory
      }
      taxFormAnswers={taxFormAnswers}
      taxFormQuestions={taxFormQuestions}
      testHistory={testHistory}
      regulators={regulators}
    />
  )
}

interface Props {
  taxFormAnswers: TestAnswersDto | undefined
  taxFormQuestions: ClientTestDto | undefined
  regulators: RegulatorOptionDto[]
  testHistory: TaxHistoryDto[]
  account: AccountDetailedDto | undefined
  isLoading?: boolean
}

export const W8Beneficial: React.FC<Props> = ({
  taxFormAnswers,
  taxFormQuestions,
  regulators,
  testHistory,
  account,
  isLoading,
}) => {
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const isArabic = useArabicSessionLanguage()
  const actionsState = useState(false)
  const historyModal = useModal(TestStatusHistoryModal, {
    history: testHistory,
  })
  const downloadForm = useDownloadW8BForm({
    taxFormAnswers,
    isArabic,
    regulators,
    taxFormQuestions,
  })

  const [submitted, setSubmitted] = useState(false)

  const handleFormSubmit = async (values: W8BeneficialFormValues) => {
    const clientId = account?.id || ''

    const payload = {
      testId: taxFormQuestions?.id || '',
      category: taxFormQuestions?.category || '',
      clientId,
      dateTaken: new Date().toISOString(),
      freeAnswers: values.freeAnswers.filter((item) => item.answer),
      selectedAnswers: values.selectedAnswers.filter((answerId) => !!answerId),
    }
    try {
      await apiClient.createClientTests(payload)
      await queryClient.invalidateQueries({ queryKey: ['profile'] })
      await queryClient.invalidateQueries({ queryKey: ['account'] })
      setSubmitted(true)
    } catch (error: unknown) {
      console.error(error)
    }
  }

  return (
    <Loading showLoadingIcon isLoading={!!isLoading}>
      {submitted && (
        <StatusTemplateWrapper>
          <StatusTemplateBody
            title={t('W8.Thank You!')}
            text={[t('W8.W-8BEN Form successfully submitted')]}
            icon={'Success'}
          />
          <StatusTemplateButtons>
            <Button.Primary size='lg' onClick={() => navigate(CommonPaths.TradersRoomWallets)}>
              {t('W8.Go To Dashboard')}
            </Button.Primary>
          </StatusTemplateButtons>
        </StatusTemplateWrapper>
      )}
      {!submitted &&
        taxFormQuestions &&
        account && [
          <PageSection
            title={t('W-8BEN FORM')}
            renderActions={
              taxFormAnswers
                ? [
                    {
                      icon: KebabIcon,
                      dropdown: {
                        state: actionsState,
                        options: [
                          {
                            label: t('Download form'),
                            onClick: () => {
                              downloadForm()
                              actionsState[1](false)
                            },
                            icon: ArrowCircleIcon.Down,
                          },
                          {
                            label: t('History'),
                            onClick: () => {
                              historyModal.open()
                              actionsState[1](false)
                            },
                            icon: ClockIcon,
                          },
                        ],
                      },
                    },
                  ]
                : undefined
            }
          />,
          <Box pt='x8'>
            <AlertNotice
              id='w8-beneficial-alert'
              message={t('W8.To be able to view and trade the CFD Stocks that are available')}
              type='info'
            />
          </Box>,
          <W8BeneficialForm
            regulators={regulators}
            taxFormQuestions={taxFormQuestions}
            taxFormAnswers={taxFormAnswers}
            account={account}
            onFormSubmit={handleFormSubmit}
            isFormEnabled={!taxFormAnswers || taxFormAnswers?.allowRetake}
          />,
        ]}
    </Loading>
  )
}
